import type React from 'react';
import { useIntl } from 'react-intl';

import type { VitaPersonalInformationFragment } from '@xing-com/crate-lebenslauf-graphql';

import { icons } from '..';

import {
  FieldContainer,
  Value,
  IconContainer,
  LabelContainer,
} from './template-personal-data.styled';

type FieldProps = {
  Icon?: typeof icons.IconHome | null;
  label?: string | null;
  value?: string | JSX.Element | null;
  birthdate?: string | null;
  birthplace?: string | null;
};

type FieldAddressProps = {
  Icon?: typeof icons.IconHome | null;
  city?: string | null;
  zip?: string | null;
  street?: string | null;
  country?: string | null;
  label?: string | null;
  sameLine?: boolean;
};

const formatCalendar = (
  date?: string | null,
  place?: string | null
): string => {
  if (date && place) {
    return date + ' in ' + place; // TODO: add copy here
  }
  return date || place || '';
};

const TranslatedField: React.FC<FieldProps> = ({
  Icon,
  label,
  value,
  birthdate,
  birthplace,
}) => {
  const intl = useIntl();

  if (!value && !birthdate && !birthplace) {
    return null;
  }
  const formatedBirthDate = birthdate
    ? intl.formatDate(birthdate, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      })
    : null;
  const formatedBirthDateCalendar =
    birthdate || birthplace
      ? formatCalendar(formatedBirthDate, birthplace)
      : null;

  return (
    <FieldContainer>
      {Icon && (
        <IconContainer>
          <Icon height={13} />
        </IconContainer>
      )}
      {label && (
        <LabelContainer>{intl.formatMessage({ id: label })}</LabelContainer>
      )}
      <Value>{formatedBirthDateCalendar || value}</Value>
    </FieldContainer>
  );
};

const TranslatedFieldAddress: React.FC<FieldAddressProps> = ({
  Icon,
  city,
  zip,
  street,
  label,
  sameLine,
}) => {
  const intl = useIntl();

  if (!street && !zip && !city) {
    return null;
  }
  const value = sameLine ? (
    <div>
      <>
        {street ? (
          <>
            {street}, {[zip, city].join(' ')}
          </>
        ) : (
          [zip, city].join(' ')
        )}
      </>
    </div>
  ) : (
    <>
      <div>{street}</div>
      <div>{[zip, city].join(' ')}</div>
    </>
  );

  return (
    <TranslatedField
      Icon={Icon}
      label={label ? intl.formatMessage({ id: label }) : null}
      value={value}
    />
  );
};

export const getPersonalDataItems = ({
  hasLabel,
  phone,
  email,
  city,
  zip,
  street,
  country,
  birthdate,
  birthplace,
  oneSingelAdressDataLine,
}: VitaPersonalInformationFragment & {
  hasLabel?: boolean;
  oneSingelAdressDataLine?: boolean;
}): JSX.Element[] => {
  const items: JSX.Element[] = [];

  if (street || zip || city) {
    items.push(
      <TranslatedFieldAddress
        key="street"
        Icon={hasLabel ? null : icons.IconHome}
        label={hasLabel ? 'CRATE_VITA_TEMPLATES_LABEL_ADDRESS' : null}
        street={street}
        zip={zip}
        city={city}
        sameLine={oneSingelAdressDataLine}
      />
    );
  }
  if (country) {
    items.push(
      <TranslatedField
        key="country"
        Icon={hasLabel ? null : icons.IconLocationPin}
        label={hasLabel ? 'CRATE_VITA_TEMPLATES_LABEL_COUNTRY' : null}
        value={country}
      />
    );
  }
  if (phone) {
    items.push(
      <TranslatedField
        key="tel"
        Icon={hasLabel ? null : icons.IconPhone}
        label={hasLabel ? 'CRATE_VITA_TEMPLATES_LABEL_PHONE' : null}
        value={phone}
      />
    );
  }
  if (email) {
    items.push(
      <TranslatedField
        key="email"
        Icon={hasLabel ? null : icons.IconEmail}
        label={hasLabel ? 'CRATE_VITA_TEMPLATES_LABEL_EMAIL' : null}
        value={email}
      />
    );
  }
  if (birthdate || birthplace) {
    items.push(
      <TranslatedField
        key="birth"
        Icon={hasLabel ? null : icons.IconEvents}
        label={hasLabel ? 'CRATE_VITA_TEMPLATES_LABEL_BIRTH' : null}
        birthdate={birthdate}
        birthplace={birthplace}
      />
    );
  }

  return items;
};
